import { inject, onBeforeUnmount, provide, ref } from 'vue'
import type {
  AttachmentFile,
  PromptInputContext,
  PromptInputMessage,
} from './types'
import { PROMPT_INPUT_KEY } from './types'

function createId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
}

export function usePromptInputProvider(props: {
  initialInput?: string
  maxFiles?: number
  maxFileSize?: number
  accept?: string
  onSubmit?: (message: PromptInputMessage) => void | Promise<void>
  onError?: (err: { code: string; message: string }) => void
}) {
  const textInput = ref(props.initialInput || '')
  const files = ref<AttachmentFile[]>([])
  const fileInputRef = ref<HTMLInputElement | null>(null)
  const isLoading = ref(false)

  onBeforeUnmount(() => {
    files.value.forEach((file) => {
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url)
      }
    })
  })

  const setTextInput = (val: string) => {
    textInput.value = val
  }

  const matchesAccept = (file: File) => {
    if (!props.accept || props.accept.trim() === '') return true

    const acceptedTypes = props.accept
      .split(',')
      .map((type) => type.trim())
      .filter(Boolean)

    return acceptedTypes.some((acceptedType) => {
      if (acceptedType === 'image/*') return file.type.startsWith('image/')
      if (acceptedType.endsWith('/*')) {
        return file.type.startsWith(acceptedType.slice(0, -1))
      }
      if (acceptedType.startsWith('.')) {
        return file.name.toLowerCase().endsWith(acceptedType.toLowerCase())
      }

      return file.type === acceptedType
    })
  }

  const addFiles = (incoming: File[] | FileList) => {
    const fileList = Array.from(incoming)

    const accepted = fileList.filter(matchesAccept)
    if (fileList.length && accepted.length === 0) {
      props.onError?.({
        code: 'accept',
        message: 'No files match the accepted types.',
      })
      return
    }

    const sized = accepted.filter((file) =>
      props.maxFileSize ? file.size <= props.maxFileSize : true
    )
    if (accepted.length > 0 && sized.length === 0) {
      props.onError?.({
        code: 'max_file_size',
        message: 'All files exceed the maximum size.',
      })
      return
    }

    const currentCount = files.value.length
    const capacity = props.maxFiles
      ? Math.max(0, props.maxFiles - currentCount)
      : undefined
    const capped =
      typeof capacity === 'number' ? sized.slice(0, capacity) : sized

    if (typeof capacity === 'number' && sized.length > capacity) {
      props.onError?.({
        code: 'max_files',
        message: 'Too many files. Some were not added.',
      })
    }

    const newAttachments: AttachmentFile[] = capped.map((file) => ({
      id: createId(),
      type: 'file',
      url: URL.createObjectURL(file),
      mediaType: file.type,
      filename: file.name,
      file,
    }))

    files.value = [...files.value, ...newAttachments]
  }

  const removeFile = (id: string) => {
    const file = files.value.find((item) => item.id === id)
    if (file?.url && file.url.startsWith('blob:')) {
      URL.revokeObjectURL(file.url)
    }
    files.value = files.value.filter((item) => item.id !== id)
  }

  const clearFiles = () => {
    files.value.forEach((file) => {
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url)
      }
    })
    files.value = []
  }

  const clearInput = () => {
    textInput.value = ''
  }

  const openFileDialog = () => {
    fileInputRef.value?.click()
  }

  const convertBlobUrlToDataUrl = async (
    url: string
  ): Promise<string | null> => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()

      return await new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(blob)
      })
    } catch {
      return null
    }
  }

  const submitForm = async () => {
    if (!props.onSubmit) return

    const processedFiles = await Promise.all(
      files.value.map(async (item) => {
        if (item.url && item.url.startsWith('blob:')) {
          const dataUrl = await convertBlobUrlToDataUrl(item.url)
          return { ...item, url: dataUrl ?? item.url }
        }

        return item
      })
    )

    const message = {
      text: textInput.value,
      files: processedFiles,
    }

    try {
      isLoading.value = true
      await props.onSubmit(message)
      clearInput()
      clearFiles()
    } catch (e) {
      const errorMessage =
        e instanceof Error
          ? e.message
          : String(e) || 'An unknown error occurred during submission.'

      props.onError?.({
        code: 'submit_error',
        message: errorMessage,
      })
      console.error('Submission failed:', e)
    } finally {
      isLoading.value = false
    }
  }

  const context: PromptInputContext = {
    textInput,
    files,
    fileInputRef,
    isLoading,
    setTextInput,
    addFiles,
    removeFile,
    clearFiles,
    clearInput,
    openFileDialog,
    submitForm,
  }

  provide(PROMPT_INPUT_KEY, context)
  return context
}

export function usePromptInput() {
  const context = inject(PROMPT_INPUT_KEY)

  if (!context) {
    throw new Error(
      'usePromptInput must be used within a PromptInput component'
    )
  }

  return context
}
